import React, { useState } from 'react';
import { useSite } from '../content/SiteContext';
import { useApplications } from '../components/ApplicationsContext';
import { sendToWhatsApp, formatMessage, isValidEmail, isValidUaeMobile } from '../lib/contact';
import { PageMeta, PageHero, SectionHead, Icon, Field, describedBy, CheckList } from '../components/ui';

const TYPES = ['Individual car washer', 'Car wash / detailing company'];
const EXPERIENCE = ['No experience', 'Less than 1 year', '1–3 years', '3+ years'];
const REQUIREMENTS = ['Valid UAE driving licence', 'Reliable transportation', 'Smartphone with a data plan', 'Attention to detail', 'Professional, customer-friendly attitude', 'Experience preferred but not required'];

const EMPTY = { type: TYPES[0], fullName: '', company: '', email: '', phone: '', city: '', experience: '', motivation: '' };

const Partner = () => {
  const { CITIES } = useSite();
  const { submitApplication } = useApplications();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sentUrl, setSentUrl] = useState(null);
  const isCompany = form.type === TYPES[1];

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (sentUrl) return;
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Enter your full name.';
    if (isCompany && !form.company.trim()) e.company = 'Enter your company name.';
    if (!isValidEmail(form.email)) e.email = 'Enter a valid email address.';
    if (!isValidUaeMobile(form.phone)) e.phone = 'Enter a UAE mobile number, for example 050 123 4567.';
    if (!form.city) e.city = 'Choose where you would work.';
    if (!form.experience) e.experience = 'Choose your experience level.';
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    submitApplication(form);
    const text = formatMessage('Quick Way partner application', [
      ['Applying as', form.type], ['Name', form.fullName], ['Company', isCompany && form.company], ['Email', form.email],
      ['Phone', form.phone], ['Emirate', form.city], ['Experience', form.experience], ['About me', form.motivation]
    ]);
    setSentUrl(sendToWhatsApp(text, 'generate_lead', { lead_type: 'partner', partner_type: form.type }));
  };

  const input = (name, label, type = 'text', props = {}) => (
    <Field label={label} id={name} error={errors[name]} required={props.required}>
      <input id={name} name={name} type={type} value={form[name]} onChange={set} aria-invalid={!!errors[name]}
        aria-describedby={describedBy(name, errors[name])} {...props} />
    </Field>
  );

  return (
    <>
      <PageMeta
        title="Become a Quick Way Partner | Car Wash Jobs & Companies UAE"
        description="Join Quick Way as a mobile car washer or partner car wash company in Abu Dhabi and Dubai. See the requirements and apply on WhatsApp."
        path="/partner"
        crumbs={[{ name: 'Become a partner', path: '/partner' }]}
      />
      <PageHero eyebrow="Partners" title="Become a Quick Way partner">
        <p>We work with individual car washers and car wash companies to serve customers across Abu Dhabi and Dubai.</p>
      </PageHero>

      <section className="section">
        <div className="container split split--top">
          <div>
            <SectionHead title="Who can apply" align="left" />
            <div className="stack">
              <div className="feature feature--row"><span className="icon-tile"><Icon name="users" /></span><div><h3>Individual car washers</h3><p>Work on Quick Way bookings near you with training on our package checklists.</p></div></div>
              <div className="feature feature--row"><span className="icon-tile"><Icon name="building" /></span><div><h3>Car wash and detailing companies</h3><p>Receive bookings from Quick Way customers in the areas you cover.</p></div></div>
            </div>
            <h3 className="h-sub">Requirements</h3>
            <CheckList items={REQUIREMENTS} />
          </div>

          <div className="form-card">
            <h2 className="form-card__title">Apply now</h2>
            {sentUrl ? (
              <div className="form-success" role="status">
                <Icon name="chat" size={28} />
                <h3>Almost done: press send in WhatsApp</h3>
                <p>Your application is ready in WhatsApp. It reaches us once you press send. If WhatsApp did not open, use the button below.</p>
                <a href={sentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">Open WhatsApp</a>
                <button type="button" className="btn-link" onClick={() => { setSentUrl(null); setForm(EMPTY); }}>Start a new application</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <fieldset className="field">
                  <legend className="field__label">Applying as</legend>
                  <div className="choice-row">
                    {TYPES.map((t) => (
                      <label key={t} className={`chip${form.type === t ? ' is-selected' : ''}`}>
                        <input type="radio" name="type" value={t} checked={form.type === t} onChange={set} /> {t}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="form-grid">
                  {input('fullName', 'Full name', 'text', { required: true, autoComplete: 'name' })}
                  {isCompany && input('company', 'Company name', 'text', { required: true, autoComplete: 'organization' })}
                  {input('email', 'Email', 'email', { required: true, autoComplete: 'email' })}
                  {input('phone', 'Mobile number', 'tel', { required: true, autoComplete: 'tel', inputMode: 'tel', placeholder: '050 123 4567' })}
                  <Field label="Emirate" id="city" error={errors.city} required>
                    <select id="city" name="city" value={form.city} onChange={set} aria-invalid={!!errors.city} aria-describedby={describedBy('city', errors.city)}>
                      <option value="">Select</option>
                      {CITIES.map((c) => <option key={c.id}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Experience" id="experience" error={errors.experience} required>
                    <select id="experience" name="experience" value={form.experience} onChange={set} aria-invalid={!!errors.experience} aria-describedby={describedBy('experience', errors.experience)}>
                      <option value="">Select</option>
                      {EXPERIENCE.map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Tell us about yourself" id="motivation">
                  <textarea id="motivation" name="motivation" rows="3" value={form.motivation} onChange={set} />
                </Field>
                <button type="submit" className="btn btn-primary btn-block btn-lg">Send application via WhatsApp</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Partner;
